import { Dropzone, DropzoneProps } from '@mantine/dropzone';
import { useController, useFormContext } from 'react-hook-form';
import { Text } from '@mantine/core';
import { BetaIcon } from '../../../../lib/components';
import { useTranslation } from 'react-i18next';
import { Flex } from '@mantine/core';

type FileInputProps = Omit<DropzoneProps, 'onDrop'> & {
  name: string;
  label?: string;
  description?: string;
};

type FormValues = {
  [key: string]: File[];
};

export const FileInput = ({
  name,
  label,
  description,
  ...props
}: FileInputProps) => {
  const { t } = useTranslation();
  const form = useFormContext<FormValues>();
  const { field, fieldState } = useController({
    name,
    control: form.control,
  });

  const files = Array.isArray(field.value) ? field.value : [];

  return (
    <div>
      <Dropzone
        data-testid="file-dropzone"
        styles={{
          root: {
            border: '1px dashed var(--mantine-color-dark-6)',
            padding: '12px',
            cursor: 'pointer',
          },
          inner: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
          },
        }}
        onDrop={(files) => {
          field.onChange(files);
        }}
        {...props}
        {...field}
      >
        <BetaIcon icon="upload" />
        <Text size="sm">{label || 'Drop files here or Browse files'}</Text>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
      </Dropzone>
      {fieldState.error?.message && (
        <Text size="xs" c="red" mt={4}>
          {fieldState.error.message}
        </Text>
      )}
      {files.length > 0 && (
        <Flex justify="space-between" align="center" mt={4} c="dimmed">
          <Text size="xs">
            {t('plots.import.selected', { count: files.length })}{' '}
            {files.map((file) => file.name).join(', ')}
          </Text>
          <BetaIcon
            icon="close"
            size="sm"
            onClick={() => field.onChange([])}
            cursor="pointer"
          />
        </Flex>
      )}
    </div>
  );
};
